import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';
  // Hidden-by-default sidebar entries (e.g. Tenants) are only rendered
  // when their key is present in this set. Toggled from the devtools
  // console via window.showNav('tenants') / hideNav('tenants').
  hiddenNavEnabled: Record<string, boolean>;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapse: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setHiddenNavEnabled: (key: string, enabled: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'light',
      hiddenNavEnabled: {},

      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleSidebarCollapse: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setHiddenNavEnabled: (key, enabled) =>
        set((state) => {
          const next = { ...state.hiddenNavEnabled };
          if (enabled) next[key] = true;
          else delete next[key];
          return { hiddenNavEnabled: next };
        }),

      setTheme: (theme) => {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');

        let appliedTheme: 'light' | 'dark';
        if (theme === 'system') {
          appliedTheme = window.matchMedia('(prefers-color-scheme: dark)')
            .matches
            ? 'dark'
            : 'light';
        } else {
          appliedTheme = theme;
        }
        root.classList.add(appliedTheme);

        set({ theme });
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        hiddenNavEnabled: state.hiddenNavEnabled,
      }),
    }
  )
);
